import * as yup from 'yup'
import { FreeTrialRegistrationFormSchema } from '../free-trial-registration-form'
import { toast } from '@contrast-security-inc/design-system-alerts'

// Progress bar color map
const countColorMap = {
  0: 'red50',
  1: 'red50',
  2: 'red50',
  3: 'orange50',
  4: 'orange50',
  5: 'green50'
}

// Validation key messages
const validationNotEnoughCharacters = 'validation_notEnoughCharacters'
const validationNotEnoughUppercase = 'validation_notEnoughUppercase'
const validationNotEnoughLowercase = 'validation_notEnoughLowercase'
const validationNotEnoughNumbers = 'validation_notEnoughNumbers'
const validationNotEnoughSymbols = 'validation_notEnoughSymbols'

// Validation state
const validation = {
  validationNotEnoughCharacters: false,
  validationNotEnoughUppercase: false,
  validationNotEnoughLowercase: false,
  validationNotEnoughNumbers: false,
  validationNotEnoughSymbols: false
}

export interface PasswordPolicy {
  readonly min_length: number
  readonly min_upper: number
  readonly min_lower: number
  readonly min_numbers: number
  readonly min_symbols: number
}

export const defaultPasswordPolicy: PasswordPolicy = {
  min_length: 8,
  min_upper: 1,
  min_lower: 1,
  min_numbers: 2,
  min_symbols: 2
}

export const fetchPasswordPolicy = async (
  setData,
  setLoading,
  formatMessage
) => {
  try {
    const response = await fetch(
      '/Contrast/api/public/ng/free-trial/password-policy?token=' +
        window.location.hash.split('/').pop() || ''
    )
    const jsonData = await response.json()
    setData(jsonData)
    setLoading(false)
  } catch (error) {
    toast(
      formatMessage({
        id: 'detail_freeTrailPasswordPolicyFailedMsg'
      }),
      {
        variant: 'error'
      }
    )
    setLoading(false)
  }
}

// Get progress bar value and color based on validation result
export const getProgress = (result: boolean, messageId: string) => {
  validation[messageId] = result
  const count = Object.values(validation).filter(Boolean).length
  return {
    value: count,
    color: countColorMap[count]
  }
}

// Validate password based on regex and min count
export const validateRegExp = (
  regex: RegExp,
  minCount: number,
  messageId: string,
  formatMessage: any,
  setProgress: any
) => {
  return yup
    .string()
    .test(
      messageId,
      formatMessage({ id: messageId }, { count: minCount }),
      (value = '') => {
        const count = (value.match(regex) || []).length
        const result = count >= minCount
        setProgress(getProgress(result, messageId))
        return result
      }
    )
}

export const createFreeTrialRegistrationSchema = (
  formatMessage,
  setProgress,
  minLength,
  minUpper,
  minLower,
  minNumbers,
  minSymbols
): yup.SchemaOf<FreeTrialRegistrationFormSchema> =>
  yup.object({
    password: yup
      .string()
      .test(
        validationNotEnoughCharacters,
        formatMessage(
          { id: validationNotEnoughCharacters },
          { count: minLength }
        ),
        (value = '') => {
          const result = value.length >= minLength
          setProgress(getProgress(result, validationNotEnoughCharacters))
          return result
        }
      )
      .concat(
        validateRegExp(
          /[A-Z]/g,
          minUpper,
          validationNotEnoughUppercase,
          formatMessage,
          setProgress
        )
      )
      .concat(
        validateRegExp(
          /[a-z]/g,
          minLower,
          validationNotEnoughLowercase,
          formatMessage,
          setProgress
        )
      )
      .concat(
        validateRegExp(
          /\d/g,
          minNumbers,
          validationNotEnoughNumbers,
          formatMessage,
          setProgress
        )
      )
      .concat(
        validateRegExp(
          /[\W_]/g,
          minSymbols,
          validationNotEnoughSymbols,
          formatMessage,
          setProgress
        )
      )
      .required(formatMessage({ id: 'validation_requiredPassword' })),
    confirmPassword: yup
      .string()
      .equals(
        [yup.ref('password'), ''],
        formatMessage({ id: 'validation_notMatchPassword' })
      )
      .required(
        formatMessage({ id: 'validation_requiredConfirmationPassword' })
      ),
    terms: yup.boolean().oneOf([true], '').required()
  })
